"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, Search, ShoppingCart, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { CurrentUser, PublicSettings } from "@plexstore/types";

import { UserMenu } from "@/components/layout/user-menu";
import { cn } from "@/lib/utils";

interface NavbarProps {
  settings: PublicSettings;
  user: CurrentUser | null;
}

export function Navbar({ settings, user }: NavbarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => setOpen(false), [pathname]);

  const support = settings.discordInviteLink || "/reviews";
  const navItems: { name: string; link: string; external?: boolean }[] = [
    { name: "Home", link: "/" },
    { name: "Store", link: "/#catalog" },
    { name: "Products", link: "/products" },
    { name: "Support", link: support, external: support.startsWith("http") },
    { name: "Status", link: "/reviews" },
    ...settings.customNavTabs.map((tab) => ({
      name: tab.name,
      link: tab.link,
      external: !tab.link.startsWith("/") && !tab.link.startsWith("#"),
    })),
  ];

  const active = (link: string) => {
    if (link === "/") return pathname === "/";
    if (link.startsWith("/#") || link.startsWith("http")) return false;
    return pathname === link || pathname.startsWith(`${link}/`);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    router.push(value ? `/products?search=${encodeURIComponent(value)}` : "/products");
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-transparent px-3 pt-3">
      <nav
        aria-label="Primary"
        className="mx-auto flex h-14 w-full max-w-[1180px] items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#07101c]/55 px-3 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl"
      >
        <Link href="/" aria-label={settings.storeName} className="flex shrink-0 items-center gap-2 pl-1">
          <Image src={settings.logoPath || "/images/logo.png"} alt="" width={28} height={28} className="size-7 object-contain" priority />
          <span className="text-[13px] font-bold tracking-[0.16em] text-white uppercase">{settings.storeName}</span>
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => {
            const className = cn(
              "rounded-lg px-3 py-1.5 text-[13px] text-white/75 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2f7cff]",
              active(item.link) && "bg-[#12305f]/80 text-white shadow-[inset_0_-2px_0_#2f7cff]",
            );
            return (
              <li key={item.name + item.link}>
                {item.external ? (
                  <a href={item.link} target="_blank" rel="noreferrer" className={className}>
                    {item.name}
                  </a>
                ) : (
                  <Link href={item.link} className={className}>
                    {item.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-1.5">
          {searchOpen ? (
            <form onSubmit={submitSearch} className="hidden sm:block">
              <label className="sr-only" htmlFor="store-search">
                Search products
              </label>
              <input
                id="store-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                className="h-9 w-36 rounded-lg border border-white/15 bg-black/40 px-3 text-sm text-white outline-none focus:border-[#2f7cff]"
              />
            </form>
          ) : null}
          <button
            type="button"
            aria-label={searchOpen ? "Close search" : "Search products"}
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((value) => !value)}
            className="grid size-9 place-items-center rounded-lg text-white/80 hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2f7cff]"
          >
            <Search className="size-4" />
          </button>
          <Link
            href="/cart"
            aria-label={user && user.cartCount > 0 ? `Cart, ${user.cartCount} items` : "Cart"}
            className="relative grid size-9 place-items-center rounded-lg text-white/80 hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2f7cff]"
          >
            <ShoppingCart className="size-4" />
            {user && user.cartCount > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full bg-[#2f7cff] px-1 text-[10px] font-bold text-white">
                {user.cartCount > 9 ? "9+" : user.cartCount}
              </span>
            ) : null}
          </Link>
          {user ? (
            <UserMenu user={user} />
          ) : (
            <Link
              href="/login"
              className="inline-flex h-9 items-center rounded-lg bg-[#2f7cff] px-4 text-sm font-semibold text-white shadow-[0_0_18px_rgba(47,124,255,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Login
            </Link>
          )}
          <button
            type="button"
            className="grid size-9 place-items-center rounded-lg text-white lg:hidden"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </div>
      </nav>
      {open ? (
        <div className="mx-auto mt-2 w-full max-w-5xl rounded-2xl border border-white/10 bg-[#07101c]/95 p-3 backdrop-blur-xl lg:hidden">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) =>
              item.external ? (
                <li key={item.name + item.link}>
                  <a href={item.link} className="block rounded-lg px-3 py-2 text-sm text-white/80" target="_blank" rel="noreferrer">
                    {item.name}
                  </a>
                </li>
              ) : (
                <li key={item.name + item.link}>
                  <Link href={item.link} className="block rounded-lg px-3 py-2 text-sm text-white/80">
                    {item.name}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </div>
      ) : null}
    </header>
  );
}
